import { Select, SelectContent } from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import * as SelectPrimitive from "@radix-ui/react-select"
import type { ReactNode } from "react"
import type { Geltung } from "../mapping/geltungMapping"
import { ExplainedSelectItem } from "./ExplainedSelectItem"

export const GELTUNG_ORDER: Geltung[] = [
  "all",
  "list",
  "group",
  "material",
  "ask",
]

export const GELTUNG_KEY: Record<Geltung, TranslationKeys> = {
  all: "MC_DECL_V2_GELTUNG_ALL",
  list: "MC_DECL_V2_GELTUNG_LIST",
  group: "MC_DECL_V2_GELTUNG_GROUP",
  material: "MC_DECL_V2_GELTUNG_MATERIAL",
  ask: "MC_DECL_V2_GELTUNG_ASK",
}

/** Per-option explainer, rendered as a muted second line inside each
 *  `SelectItem` (see the item markup below) — the five Geltung values read
 *  as short labels alone, this is the one-line "what does choosing this
 *  actually mean" a reviewer gets alongside it. */
export const GELTUNG_EXPLAINER_KEY: Record<Geltung, TranslationKeys> = {
  all: "MC_DECL_V2_GELTUNG_ALL_EXPLAINER",
  list: "MC_DECL_V2_GELTUNG_LIST_EXPLAINER",
  group: "MC_DECL_V2_GELTUNG_GROUP_EXPLAINER",
  material: "MC_DECL_V2_GELTUNG_MATERIAL_EXPLAINER",
  ask: "MC_DECL_V2_GELTUNG_ASK_EXPLAINER",
}

export interface GeltungSelectProps {
  value: Geltung
  onChange: (next: Geltung) => void
  disabled?: boolean
  /** A scope edit on this card is in flight (TUR-1211, W5-5): the
   *  optimistic patch already shows the new Geltung, this is the quiet
   *  saving state on the control itself. */
  "aria-busy"?: boolean
  /** Sentence-form label for the trigger ("laut Liste" inside the card's
   *  header sentence, `HeaderSentence.tsx`); the popup keeps the full labels.
   *  Omitted: the plain catalog label, as before. */
  triggerLabel?: ReactNode
}

/**
 * The per-unit Geltung control — all five display values, always offered
 * regardless of the unit's current one: `group`/`material` are how the
 * reviewer chooses to READ a `list` scope (no write of their own,
 * `geltungMapping.ts`), and `ask` never writes at all — its handler opens the
 * placeholder Rückfrage card instead.
 *
 * WAS A NATIVE `<select>` behind an invisible "ghost" overlay (`sv2-gsel` —
 * the pill span the reviewer actually sees — with a fully transparent
 * `<select class="sv2-gsel-ghost">` absolutely positioned on top, because a
 * plain `<select>` cannot render a rounded pill directly). The product owner
 * flagged the resulting OS-native menu with a screenshot — dark app, light
 * system dropdown. Radix's `Trigger` needs no such trick: it is a real,
 * focusable element that accepts ARBITRARY children, so the pill markup
 * (label text + `▾`) now lives directly on the trigger and the ghost overlay
 * is gone outright, not just re-skinned. `sv2-gselwrap`'s own CSS
 * (`position: relative`, sizing) still wraps it unchanged, so the pill's
 * on-page footprint is pixel-identical to before.
 *
 * `SelectContent` (the popup) uses `@/components/ui/select`'s own styling —
 * the app's global `bg-popover` tokens, not `.sv2-root`-scoped ones — so it
 * is safe to portal to `document.body` (see `VerdictSelect`'s doc for the
 * full portal reasoning, which applies identically here).
 */
export function GeltungSelect({
  value,
  onChange,
  disabled,
  "aria-busy": ariaBusy,
  triggerLabel,
}: GeltungSelectProps) {
  const { translate } = useTranslation()
  return (
    <span className="sv2-gselwrap">
      <Select
        value={value}
        onValueChange={(v) => onChange(v as Geltung)}
        disabled={disabled}
      >
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            {/* The tooltip sits on a WRAPPING span, not `asChild` directly on
                `SelectPrimitive.Trigger`: Radix Tooltip's `Slot` cloning onto
                the Select trigger button breaks its own click-to-open (proved
                empirically — `UnitCard.test.tsx`'s `pickOption` clicks stopped
                opening the popup, `aria-expanded` stuck at `false`). A plain
                wrapper avoids the clone entirely; the trigger button
                underneath keeps its own untouched click handling. */}
            <TooltipTrigger asChild>
              {/* `inline-flex`, not a bare span: `.sv2-gselwrap` (this
                  component's own outer wrapper) promises the pill's on-page
                  footprint stays pixel-identical to the pre-Tooltip markup —
                  a bare span blockifies as a flex item and can add line-box
                  leading `.sv2-gsel` never had. */}
              <span className="inline-flex">
                <SelectPrimitive.Trigger
                  aria-label={translate("MC_DECL_V2_GELTUNG_SELECT_ARIA")}
                  aria-busy={ariaBusy}
                  className="sv2-gsel"
                >
                  {triggerLabel ?? translate(GELTUNG_KEY[value])}
                  <span className="sv2-car">▾</span>
                </SelectPrimitive.Trigger>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              {translate("MC_DECL_V2_GELTUNG_SELECT_ARIA")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <SelectContent>
          {GELTUNG_ORDER.map((g) => (
            <ExplainedSelectItem
              key={g}
              value={g}
              label={translate(GELTUNG_KEY[g])}
              explainer={translate(GELTUNG_EXPLAINER_KEY[g])}
            />
          ))}
        </SelectContent>
      </Select>
    </span>
  )
}
